import React, { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import api from "@/lib/api";
import { format, formatDistanceToNow } from "date-fns";
import { ShieldCheck, FilePlus, Pencil, Mail, GitCompareArrows, FileDown, LogIn, LogOut, UserPlus, Archive, ArchiveRestore, Lock, AlertTriangle, Eye, History, Users as UsersIcon } from "lucide-react";

const ICON = {
  "auth.login":           LogIn,
  "auth.login_failed":    AlertTriangle,
  "auth.register":        UserPlus,
  "auth.logout":          LogOut,
  "auth.locked":          Lock,
  "report.view":          Eye,
  "report.edit":          Pencil,
  "report.revert":        History,
  "report.share_on":      ShieldCheck,
  "report.share_off":     ShieldCheck,
  "report.email":         Mail,
  "report.pdf":           FileDown,
  "report.archive":       Archive,
  "report.restore":       ArchiveRestore,
  "report.delete":        AlertTriangle,
  "patient.create":       UserPlus,
  "patient.update":       Pencil,
  "patient.archive":      Archive,
  "patient.restore":      ArchiveRestore,
  "patient.delete":       AlertTriangle,
  "patient.timeline_pdf": FileDown,
  "comparison.create":    GitCompareArrows,
};

const COLOR = {
  "auth.login":           "var(--color-primary)",
  "auth.login_failed":    "var(--color-warning)",
  "auth.register":        "var(--color-primary)",
  "auth.logout":          "var(--color-text-secondary)",
  "auth.locked":          "var(--color-error)",
  "report.view":          "var(--color-info)",
  "report.edit":          "var(--color-accent)",
  "report.revert":        "var(--color-accent)",
  "report.share_on":      "var(--color-primary)",
  "report.share_off":     "var(--color-text-secondary)",
  "report.email":         "var(--color-info)",
  "report.pdf":           "var(--color-secondary)",
  "report.archive":       "var(--color-text-secondary)",
  "report.restore":       "var(--color-primary)",
  "report.delete":        "var(--color-error)",
  "patient.create":       "var(--color-primary)",
  "patient.update":       "var(--color-accent)",
  "patient.archive":      "var(--color-text-secondary)",
  "patient.restore":      "var(--color-primary)",
  "patient.delete":       "var(--color-error)",
  "patient.timeline_pdf": "var(--color-secondary)",
  "comparison.create":    "var(--color-secondary)",
};

export default function AuditPage() {
  const [events, setEvents] = useState(null);
  const [actions, setActions] = useState([]);
  const [filter, setFilter] = useState(null);

  const load = async (action) => {
    setEvents(null);
    const [{ data }, actionsRes] = await Promise.all([
      api.get("/audit-log", { params: action ? { action } : {} }),
      api.get("/audit-log/actions"),
    ]);
    setEvents(data);
    setActions(actionsRes.data);
  };

  useEffect(() => {
    load(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const totalCount = useMemo(() => actions.reduce((s, a) => s + (a.count || 0), 0), [actions]);

  return (
    <Layout>
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>Compliance</div>
        <div className="flex flex-wrap items-end justify-between gap-4 mt-2">
          <h1 className="font-display text-3xl md:text-4xl font-semibold">Audit log</h1>
          <p className="text-sm max-w-md" style={{ color: "var(--color-text-secondary)" }}>
            A chronological trail of every protected action you&apos;ve performed in your workspace. Useful for HIPAA / SOC2 review.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="audit-filter-row">
        <FilterChip
          active={filter === null}
          onClick={() => setFilter(null)}
          label={`All · ${totalCount}`}
          testid="audit-filter-all"
        />
        {actions.map((a) => (
          <FilterChip
            key={a.action}
            active={filter === a.action}
            onClick={() => setFilter(a.action)}
            label={`${a.label} · ${a.count}`}
            testid={`audit-filter-${a.action}`}
          />
        ))}
      </div>

      {events === null && (
        <div className="bg-white border rounded-lg p-12 flex justify-center" style={{ borderColor: "var(--color-border)" }}>
          <div className="spinner" />
        </div>
      )}

      {events !== null && events.length === 0 && (
        <div className="bg-white border rounded-lg p-12 text-center" style={{ borderColor: "var(--color-border)" }}>
          <ShieldCheck className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--color-secondary)" }} />
          <p className="font-display text-lg">No events recorded yet</p>
          <p className="text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>Activity will appear here as soon as you use the app.</p>
        </div>
      )}

      {events !== null && events.length > 0 && (
        <ol className="relative bg-white border rounded-lg px-6 py-4" style={{ borderColor: "var(--color-border)" }}>
          <span className="absolute top-6 bottom-6 w-px" style={{ left: "2.15rem", background: "var(--color-border)" }} aria-hidden />
          {events.map((e) => (
            <EventRow key={e.log_id} event={e} />
          ))}
        </ol>
      )}
    </Layout>
  );
}

function FilterChip({ active, onClick, label, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="text-xs px-3 py-1.5 rounded-full border transition"
      style={{
        borderColor: active ? "var(--color-primary)" : "var(--color-border)",
        background: active ? "var(--color-primary)" : "var(--bg-surface)",
        color: active ? "#fff" : "var(--color-text-primary)",
      }}
    >
      {label}
    </button>
  );
}

function EventRow({ event }) {
  const Icon = ICON[event.action] || ShieldCheck;
  const color = COLOR[event.action] || "var(--color-text-secondary)";
  const when = event.at ? new Date(event.at) : null;
  const whenAbs = when ? format(when, "d MMM yyyy · HH:mm") : "—";
  const whenRel = when ? formatDistanceToNow(when, { addSuffix: true }) : "";

  const detail = describeMeta(event);

  return (
    <li
      className="relative flex items-start gap-4 py-4 first:pt-2 last:pb-2"
      data-testid={`audit-event-${event.action}`}
    >
      <div
        className="relative z-10 flex items-center justify-center w-9 h-9 rounded-full border shrink-0"
        style={{ borderColor: "var(--color-border)", background: "var(--bg-surface)" }}
      >
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-xs uppercase tracking-widest" style={{ color }}>{event.action.split(".")[0]}</span>
          <span className="font-medium">{event.label || event.action}</span>
        </div>
        {detail && (
          <p className="text-sm mt-0.5 truncate" style={{ color: "var(--color-text-secondary)" }}>{detail}</p>
        )}
        <div className="text-xs mt-1" style={{ color: "var(--color-text-secondary)" }}>
          {whenAbs}{whenRel && <span className="opacity-70"> · {whenRel}</span>}
          {event.ip && <span className="opacity-70"> · {event.ip}</span>}
        </div>
      </div>
    </li>
  );
}

function describeMeta(e) {
  const m = e.meta || {};
  const bits = [];
  if (e.resource_type && e.resource_id) bits.push(`${e.resource_type} ${e.resource_id}`);
  if (m.language) bits.push(`Lang: ${String(m.language).toUpperCase()}`);
  if (m.mode) bits.push(`Mode: ${m.mode}`);
  if (m.to) bits.push(`To: ${m.to}`);
  if (m.scope) bits.push(`Scope: ${m.scope}`);
  if (Array.isArray(m.scopes) && m.scopes.length) bits.push(`Scopes: ${m.scopes.join(", ")}`);
  if (Array.isArray(m.fields) && m.fields.length) bits.push(`Fields: ${m.fields.join(", ")}`);
  if (typeof m.count === "number") bits.push(`Count: ${m.count}`);
  if (m.reason) bits.push(`Reason: ${m.reason}`);
  if (m.attempts) bits.push(`Attempts: ${m.attempts}`);
  return bits.join(" · ") || null;
}
